/**
 * "+ NEW" - the Articles page's one way to start something (v1.0.29, the designer's pill and its
 * menu of icon rows): write an article, and a video row that follows the author's video access -
 * "Upload video" once staff granted it, "Request access to video" before anyone asked, and a
 * dimmed "Video access requested" while staff have not answered. An author who may not ask yet is
 * told why instead: an applicant that video comes after approval, an author on hold that it waits
 * for the hold to lift.
 *
 * A "Live stream" row (1.2.0) does the same by the author's stream access, a switch of its own:
 * granted, it starts a live stream draft; otherwise it asks, waits or says why, as video's does.
 *
 * ★ A ROW THAT ONLY INFORMS STAYS REACHABLE. Mantine leaves a `disabled` item out of the arrow
 * keys, so its note could never be read from the keyboard: such a row is `aria-disabled`, dimmed
 * by its own class, and its click does nothing.
 */
import { Button, Menu, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { Hourglass, PenLine, Play, Plus, Video } from '@moonx/ui';
import type { AuthorSession, VideoAccess } from '../../features/cabinet/api/generated';
import { useTranslation } from '../../i18n';
import type { VideoKey } from '../videos/video-i18n';
import classes from './articles.module.css';

export interface NewMenuProps {
  /** Undefined while the session is read: the video row waits for it. */
  readonly videoAccess: VideoAccess | undefined;
  /** Live streams' switch (1.2.0), read with the session: the stream row waits for it too. */
  readonly streamAccess: VideoAccess | undefined;
  /**
   * Where the author stands. Only an approved author may ask for video; for an applicant or an
   * author on hold the row says when they can instead. Undefined while it is read.
   */
  readonly authorStatus?: AuthorSession['authorStatus'] | undefined;
  /** A row's work is running: the pill spins and the menu stays shut. */
  readonly busy: boolean;
  readonly onWriteArticle: () => void;
  readonly onUploadVideo: () => void;
  readonly onRequestAccess: () => void;
  readonly onStartStream: () => void;
  readonly onRequestStreamAccess: () => void;
}

/** A row's two lines: what it does, and a quiet line under it. */
function RowWords({ label, note }: { label: string; note: string }) {
  return (
    <Stack gap={0} component="span">
      <Text size="sm" fw={600} component="span">
        {label}
      </Text>
      <Text size="xs" c="dimmed" component="span">
        {note}
      </Text>
    </Stack>
  );
}

/** A row that says where a switch stands and does nothing: reachable, read, and dimmed. */
function NoteRow({ icon, label, note }: { icon: ReactNode; label: string; note: string }) {
  return (
    <Menu.Item
      leftSection={icon}
      aria-disabled="true"
      className={classes.noteRow}
      closeMenuOnClick={false}
      onClick={(event) => {
        event.preventDefault();
      }}
    >
      <RowWords label={label} note={note} />
    </Menu.Item>
  );
}

/** One switch's words, a row's label and its note for each place the author can stand. */
interface AccessWords {
  readonly start: readonly [VideoKey, VideoKey];
  readonly requested: readonly [VideoKey, VideoKey];
  readonly afterApproval: readonly [VideoKey, VideoKey];
  readonly onHold: readonly [VideoKey, VideoKey];
  readonly request: readonly [VideoKey, VideoKey];
}

const VIDEO_WORDS: AccessWords = {
  start: ['new.uploadVideo', 'new.uploadVideoNote'],
  requested: ['new.accessRequested', 'new.accessRequestedNote'],
  afterApproval: ['new.afterApproval', 'new.afterApprovalNote'],
  onHold: ['new.onHold', 'new.onHoldNote'],
  request: ['new.requestAccess', 'new.requestAccessNote'],
};

const STREAM_WORDS: AccessWords = {
  start: ['new.startStream', 'new.startStreamNote'],
  requested: ['new.streamAccessRequested', 'new.streamAccessRequestedNote'],
  afterApproval: ['new.streamAfterApproval', 'new.streamAfterApprovalNote'],
  onHold: ['new.streamOnHold', 'new.streamOnHoldNote'],
  request: ['new.requestStreamAccess', 'new.requestStreamAccessNote'],
};

/** The row one switch draws: start, wait, say why, or ask - nothing while it is not read yet. */
function AccessRow({
  access,
  authorStatus,
  icon,
  words,
  onStart,
  onRequest,
}: {
  access: VideoAccess | undefined;
  authorStatus: AuthorSession['authorStatus'] | undefined;
  icon: ReactNode;
  words: AccessWords;
  onStart: () => void;
  onRequest: () => void;
}) {
  const { t } = useTranslation('video');
  const say = ([label, note]: readonly [VideoKey, VideoKey]) => ({
    label: t(label),
    note: t(note),
  });
  if (access === undefined) {
    return null;
  }
  if (access === 'granted') {
    return (
      <Menu.Item leftSection={icon} onClick={onStart}>
        <RowWords {...say(words.start)} />
      </Menu.Item>
    );
  }
  if (access === 'requested') {
    return <NoteRow icon={<Hourglass size={18} aria-hidden />} {...say(words.requested)} />;
  }
  if (authorStatus === 'applicant') {
    return <NoteRow icon={icon} {...say(words.afterApproval)} />;
  }
  if (authorStatus === 'disabled') {
    return <NoteRow icon={icon} {...say(words.onHold)} />;
  }
  return (
    <Menu.Item leftSection={icon} onClick={onRequest}>
      <RowWords {...say(words.request)} />
    </Menu.Item>
  );
}

export function NewMenu({
  videoAccess,
  streamAccess,
  authorStatus,
  busy,
  onWriteArticle,
  onUploadVideo,
  onRequestAccess,
  onStartStream,
  onRequestStreamAccess,
}: NewMenuProps) {
  const { t } = useTranslation('video');
  return (
    <Menu position="bottom-end" width={290} shadow="md" radius="md" disabled={busy}>
      <Menu.Target>
        <Button size="md" radius="xl" leftSection={<Plus size={16} aria-hidden />} loading={busy}>
          {t('new.button')}
        </Button>
      </Menu.Target>
      <Menu.Dropdown aria-label={t('new.menuLabel')}>
        <Menu.Item leftSection={<PenLine size={18} aria-hidden />} onClick={onWriteArticle}>
          <RowWords label={t('new.writeArticle')} note={t('new.writeArticleNote')} />
        </Menu.Item>
        <AccessRow
          access={videoAccess}
          authorStatus={authorStatus}
          icon={<Video size={18} aria-hidden />}
          words={VIDEO_WORDS}
          onStart={onUploadVideo}
          onRequest={onRequestAccess}
        />
        <AccessRow
          access={streamAccess}
          authorStatus={authorStatus}
          icon={<Play size={18} aria-hidden />}
          words={STREAM_WORDS}
          onStart={onStartStream}
          onRequest={onRequestStreamAccess}
        />
      </Menu.Dropdown>
    </Menu>
  );
}
